/**
 * Created by rickborst on 13/02/2017.
 */

import _ from 'lodash';

const METHOD_DEFAULTS = {
        create: 'POST',
        read: 'GET',
        update: 'UPDATE',
        destroy: 'DELETE'
    },
    ERRORS = {
        REQUEST_TIMEOUT: 'request timeout',
        REQUEST_ERROR: 'request error',
        INVALID_RESPONSE: 'invalid response'
    };

let instance;

class Api {

    /**
     * Serializes an object to a parameter string.
     *
     * @param {Object} object - To be serialized object.
     * @param {string} [prefix] - Serialize prefix.
     * @returns {string} Serialized object.
     * @private
     */
    static _serialize (object, prefix) {
        let string = [],
            k,
            v;

        Object.keys(object).forEach(function (key) {

            k = prefix ? prefix + '[' + key + ']' : key;
            v = object[key];
            string.push(typeof v === 'object'
                ? Api._serialize(v, k)
                : encodeURIComponent(k) + '=' + encodeURIComponent(v));
        });

        return string.join('&');
    }

    /**
     * Returns the request's payload.
     *
     * @param {Object} options - Options object.
     * @returns {string|null} Payload string.
     * @private
     */
    _getPayload (options) {
        let headers = options.headers || (options.headers = {}),
            payload = options.data || null;

        if (payload && options.asQuery === true) {

            payload = Api._serialize(payload);
        } else if (_.isObject(payload)
            && headers['Content-Type'] === 'application/json') {

            payload = JSON.stringify(payload);
        }

        return payload;
    }

    /**
     * Performs the xhr request.
     *
     * @param {string} url - Request url.
     * @param {string} method - Request method.
     * @param {Object} headers - Request headers.
     * @param {string|null} payload - Payload string.
     * @param {boolean} [sync] - Send synchronously..
     * @returns {Promise} Request promise.
     * @private
     */
    _doRequest (url, method, headers = {}, payload, sync) {
        return new Promise(
            (resolve, reject) => {
                let httpRequest = new XMLHttpRequest(),
                    async = !sync;

                try {

                    httpRequest.onreadystatechange =
                        this._onReadyStateChange.bind(this, httpRequest, resolve, reject);
                    httpRequest.ontimeout = reject.bind(null, {
                        type: ERRORS.REQUEST_TIMEOUT,
                        error: ERRORS.REQUEST_TIMEOUT
                    });
                    httpRequest.onerror = function (e) {
                        reject({
                            type: ERRORS.REQUEST_ERROR,
                            error: e
                        });
                    };

                    httpRequest.open(method, url, async);

                    Object.keys(headers).forEach(function (key) {
                        httpRequest.setRequestHeader(key, headers[key]);
                    });

                    httpRequest.send(payload);
                } catch (e) {
                    reject({
                        type: ERRORS.REQUEST_ERROR,
                        error: e
                    });
                }
            });
    }

    /**
     * Handles ready state changes.
     *
     * @param {Object} httpRequest - XHR request.
     * @param {Function} resolve - Resolve callback.
     * @param {Function} reject - Reject callback.
     * @private
     */
    _onReadyStateChange (httpRequest, resolve, reject) {

        if (httpRequest.readyState === XMLHttpRequest.DONE
            && httpRequest.status === 200) {

            resolve(httpRequest.responseText);
        } else if (httpRequest.readyState === XMLHttpRequest.DONE
            && httpRequest.status !== 200) {

            reject(httpRequest.responseText);
        }
    }

    /**
     * Parses response data according to the provided content type.
     *
     * @param {string} response - Response data.
     * @param {string|undefined} contentType - Content type.
     * @returns {Object} - Parsed response data.
     * @private
     */
    _parseResponse (response, contentType) {

        switch (contentType) {

            case 'application/json':
                response = JSON.parse(response);
                break;

            // TODO: Handle additional content types

        }

        return response;
    }

    /**
     * Handles positive responses.
     *
     * @param {Object} options - Options object.
     * @param {string} response - Response object.
     * @returns {Promise} Resolved or rejected promise.
     * @private
     */
    _onResponse (options, response) {
        let headers = options.headers || {};

        try {
            response = this._parseResponse(response, headers['Content-Type']);
        } catch (e) {
            return Promise.reject({
                type: ERRORS.INVALID_RESPONSE,
                error: e
            });
        }

        return Promise.resolve(response);
    }

    /**
     * Executes a create request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Create promise.
     */
    create (options = {}) {
        let method = options.method || METHOD_DEFAULTS.create,
            url = options.url,
            payload = this._getPayload(options),
            sendSynchronously = options.sync;

        return this._doRequest(url, method, options.headers, payload, sendSynchronously)
            .then(this._onResponse.bind(this, options));
    }

    /**
     * Executes a read request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Read promise.
     */
    read (options = {}) {
        let method = options.method || METHOD_DEFAULTS.read,
            url = options.url,
            payload = this._getPayload(options);

        return this._doRequest(url, method, options.headers, payload)
            .then(this._onResponse.bind(this, options));
    }

    /**
     * Executes a update request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Update promise.
     */
    update (options = {}) {
        let method = options.method || METHOD_DEFAULTS.update,
            url = options.url,
            payload = this._getPayload(options);

        return this._doRequest(url, method, options.headers, payload)
            .then(this._onResponse.bind(this, options));
    }

    /**
     * Executes a destroy request.
     *
     * @param {Object} options - Options object.
     * @returns {Promise} Destroy promise.
     */
    destroy (options = {}) {
        let method = options.method || METHOD_DEFAULTS.destroy,
            url = options.url,
            payload = this._getPayload(options);

        return this._doRequest(url, method, options.headers, payload)
            .then(this._onResponse.bind(this, options));
    }
}

/**
 * Get singleton instance of api.
 *
 * @returns {Object} API instance.
 */
export function getInstance () {

    if (!instance) {
        instance = new Api();
    }

    return instance;
}


// WEBPACK FOOTER //
// ./src/js/api/api.js